import LiveChatPanel from './LiveChatPanel';
import { authFetch } from '../utils/authFetch';
import { getOrCreateSupportStaffId } from '../utils/supportIdentity';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const STATUS_META = {
  online: { label: 'Online', dot: 'bg-green-500', pill: 'bg-green-50 text-green-800 border-green-200' },
  busy: { label: 'Delayed', dot: 'bg-yellow-500', pill: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  offline: { label: 'Offline', dot: 'bg-gray-500', pill: 'bg-gray-50 text-gray-800 border-gray-200' },
};

const SUPPORT_HELP_PANEL_ID = 'support-drawer-how-it-works';

const SUPPORT_HELP_SECTIONS = [
  {
    title: 'When Jonathan is Live (green)',
    body: (
      <>
        When Jonathan is live, <strong>Live Chat</strong> is the fastest path — you don't need to pick a category. If you want a permanent thread you can reply to later, use the <strong>Need a tracked ticket?</strong> link below the chat.
      </>
    ),
  },
  {
    title: 'Busy (yellow)',
    body: 'He’s mid-task; live chat still works but replies may be slower — usually within about an hour when things settle.',
  },
  {
    title: 'Offline (grey)',
    body: 'He’s not actively on chat. Pick a category and submit a ticket — suggested SOPs may solve the issue immediately, otherwise expect follow-up within 24–48 hours.',
  },
  {
    title: 'Urgent or emergency',
    body: 'When live chat isn’t the right fit, contact Jeff.',
  },
  {
    title: 'Tickets',
    body: (
      <>
        Use the <strong>Tickets</strong> button to see past threads, reply, and check what’s resolved.
      </>
    ),
  },
];

function shortTicketLabel(id) {
  if (!id) return '';
  return `T-${String(id).slice(-6).toUpperCase()}`;
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function SupportDrawer({
  open,
  onClose,
  supportStatus, // online|busy|offline
  categories,
  documents,
  employees,
  selectedEmployeeId,
  onSelectEmployee,
  ticketCount,
  unreadCount,
  onOpenTickets,
  onOpenSopDocument,
  onLiveChatOpenChange,
  liveChatShared,
}) {
  const drawerRef = useRef(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  // Once the user starts typing their description, hide the SOP
  // suggestions so they keep their focus on the issue.
  const [describeFocused, setDescribeFocused] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // When Jonathan is online/busy, Live Chat is the primary path. This
  // toggle discloses a tracked-ticket form beneath the chat for users
  // who specifically want a permanent thread.
  const [ticketDisclosed, setTicketDisclosed] = useState(false);

  const staffId = useMemo(() => getOrCreateSupportStaffId(), []);
  const employeeList = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    return list.filter((e) => e.active !== false);
  }, [employees]);
  const selectedEmployee = useMemo(
    () => employeeList.find((e) => e.id === selectedEmployeeId) || null,
    [employeeList, selectedEmployeeId]
  );

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (helpOpen) {
        setHelpOpen(false);
        return;
      }
      onClose?.();
    }
    if (open) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, helpOpen]);

  useEffect(() => {
    if (!open) {
      setHelpOpen(false);
      setTicketDisclosed(false);
      return;
    }
    setConfirmText('');
  }, [open]);

  // Offline collapses to ticket-only — the disclosure toggle is
  // meaningless there.
  useEffect(() => {
    if (supportStatus === 'offline') setTicketDisclosed(false);
  }, [supportStatus]);

  const mode = supportStatus || 'offline';
  const liveAvailable = mode === 'online' || mode === 'busy';
  const nameOk = !!selectedEmployee;

  // In live mode the ticket form is gated behind a disclosure; in
  // offline mode it's the primary (and only) path.
  const showTicketForm = nameOk && (!liveAvailable || ticketDisclosed);

  // Clear the category picker state when the user closes the
  // disclosure — starting fresh next time avoids a half-filled form
  // lingering behind the chat.
  useEffect(() => {
    if (!ticketDisclosed) {
      setSelectedCategoryId('');
      setMessage('');
      setDescribeFocused(false);
    }
  }, [ticketDisclosed]);

  // Live chat is considered "open" for chime-suppression whenever the
  // drawer is open with a name selected and Jonathan is available.
  // The ticket-disclosure doesn't close it — the chat stays mounted
  // as the primary view.
  useEffect(() => {
    if (!onLiveChatOpenChange) return;
    const live = open && nameOk && liveAvailable;
    onLiveChatOpenChange(live);
  }, [open, nameOk, liveAvailable, onLiveChatOpenChange]);

  const selectedCategory = useMemo(
    () => (categories || []).find((c) => c.id === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  );

  const sopSuggestions = useMemo(() => {
    // SOP suggestions are only useful when Jonathan isn't available.
    // If he's online or busy, live chat is the better path and
    // surfacing SOPs here just adds noise.
    if (mode !== 'offline') return [];
    if (!selectedCategory) return [];
    if (!Array.isArray(documents)) return [];
    const MAX = 3;

    // Manually tagged SOPs always take priority.
    const tagged = documents
      .filter((d) => !d.isArchived)
      .filter((d) => Array.isArray(d.supportCategoryIds) && d.supportCategoryIds.includes(selectedCategory.id))
      .sort((a, b) => {
        const ap = Array.isArray(a.statuses) && a.statuses.includes('Pinned') ? 1 : 0;
        const bp = Array.isArray(b.statuses) && b.statuses.includes('Pinned') ? 1 : 0;
        if (ap !== bp) return bp - ap;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    if (tagged.length >= MAX) return tagged.slice(0, MAX);

    // Fallback: word-boundary match on category-configured `sopKeywords`.
    // No category-name fallback — generic tokens like "desk" from a
    // "Front Desk" category were matching unrelated titles. Requiring
    // an admin to explicitly configure keywords keeps the signal clean.
    const pickedIds = new Set(tagged.map((d) => d.id));
    const keywords = Array.isArray(selectedCategory.sopKeywords) ? selectedCategory.sopKeywords : [];
    const terms = keywords
      .map((t) => String(t).toLowerCase().trim())
      .filter((t) => t.length >= 3);
    if (!terms.length) return tagged.slice(0, MAX);

    const regexes = terms.map((t) => new RegExp(`\\b${escapeRegex(t)}\\b`, 'i'));
    const scored = documents
      .filter((d) => !d.isArchived && !pickedIds.has(d.id))
      .map((d) => {
        const title = String(d.name || '');
        let score = 0;
        regexes.forEach((re) => { if (re.test(title)) score += 2; });
        if (Array.isArray(d.statuses) && d.statuses.includes('Pinned')) score += 1;
        return { d, score };
      })
      // Require at least one real keyword hit — a pinned doc with no
      // keyword match (score 1) shouldn't surface on its own.
      .filter((x) => x.score >= 2)
      .sort((a, b) => b.score - a.score || String(a.d.name || '').localeCompare(String(b.d.name || '')))
      .map((x) => x.d);

    return [...tagged, ...scored].slice(0, MAX);
  }, [mode, documents, selectedCategory]);

  const ticketCategorySelected = !!selectedCategoryId;
  const ticketReady = nameOk && ticketCategorySelected;
  const statusMeta = STATUS_META[mode] || STATUS_META.offline;

  async function createTicketWithMessage(text) {
    const res = await authFetch('/api/support/tickets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        createdByName: selectedEmployee?.name || '',
        createdByStaffId: staffId,
        createdByEmployeeId: selectedEmployee?.id || '',
        createdByEmail: selectedEmployee?.email || '',
        categoryId: selectedCategoryId,
        modeAtCreate: mode,
        message: text,
      }),
    });
    const json = await res.json();
    if (!res.ok || !json.ok) throw new Error(json.error || 'Failed to create ticket');
    return json.ticketId;
  }

  async function handleSendTicket() {
    const text = message.trim();
    if (!ticketReady || !text) return;
    setSending(true);
    try {
      const tid = await createTicketWithMessage(text);
      setMessage('');
      const label = shortTicketLabel(tid);
      setConfirmText(mode === 'offline'
        ? `Submitted as ${label}. Jonathan will get back to you when available. For emergencies contact Jeff. Follow up any time from the Tickets drawer.`
        : `Submitted as ${label}. Jonathan will get back to you shortly. Follow up any time from the Tickets drawer.`);
      // In live mode, collapse the disclosure after a successful
      // submit so the chat reclaims focus.
      if (liveAvailable) setTicketDisclosed(false);
    } catch (e) {
      setConfirmText(`Error: ${e.message || e}`);
    }
    setSending(false);
  }

  if (!open) return null;

  const liveBannerStyles = mode === 'online'
    ? { wrapper: 'border-emerald-200 bg-emerald-50', dot: 'bg-emerald-500', text: 'text-emerald-900' }
    : { wrapper: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', text: 'text-amber-900' };
  const livePrimaryLine = mode === 'online'
    ? 'Jonathan is online — chat live below.'
    : 'Jonathan is between tasks — live chat is available but please expect delays.';

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => onClose?.()}
        aria-label="Close support drawer overlay"
      />
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white text-gray-900 shadow-2xl border-l flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Remote support drawer"
      >
        <div className="p-4 border-b flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-gray-500">Remote Support</div>
            <div className="flex items-center gap-2 mt-1">
              <div className={`w-2.5 h-2.5 rounded-full ${statusMeta.dot}`} />
              <div className={`text-xs px-2 py-0.5 rounded-full border ${statusMeta.pill}`}>Jonathan: {statusMeta.label}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex items-center text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg px-2.5 py-1.5 border border-gray-200"
              aria-expanded={helpOpen}
              aria-controls={SUPPORT_HELP_PANEL_ID}
              title="How Remote Support works"
            >
              How it works
            </button>
            {typeof onOpenTickets === 'function' && (
              <button
                onClick={() => onOpenTickets?.()}
                className="relative inline-flex items-center gap-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg px-2.5 py-1.5 border border-gray-200"
                title="View tickets"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6m-6 4h10M5 4h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
                </svg>
                <span>Tickets</span>
                {typeof ticketCount === 'number' && ticketCount > 0 && (
                  <span className="text-[10px] text-gray-500 font-normal">{ticketCount}</span>
                )}
                {typeof unreadCount === 'number' && unreadCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white"
                    aria-label={`${unreadCount} unread ticket update${unreadCount > 1 ? 's' : ''}`}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button onClick={() => onClose?.()} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 overflow-auto flex-1">
          {/* Name picker — always the first required step. */}
          <div className="space-y-2">
            <div className="text-sm font-semibold">Who is filing this? (required)</div>
            {employeeList.length === 0 ? (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2">
                No names are available right now. Please ask your manager to set this up.
              </div>
            ) : (
              <select
                value={selectedEmployeeId || ''}
                onChange={(e) => onSelectEmployee?.(e.target.value)}
                className="w-full p-3 border rounded-lg bg-gray-50"
              >
                <option value="">Select your name…</option>
                {employeeList.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            )}
          </div>

          {!nameOk && employeeList.length > 0 && (
            <div className="text-xs text-gray-500">
              Pick your name to continue.
            </div>
          )}

          {/* LIVE MODE — Jonathan is online or busy. Live chat is the
              primary view; category picker is deliberately absent so
              staff don't think it's required. */}
          {nameOk && liveAvailable && (
            <div className="space-y-3">
              <div className={`rounded-xl border p-3 ${liveBannerStyles.wrapper}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${liveBannerStyles.dot}`} />
                  <div className={`text-sm font-semibold ${liveBannerStyles.text}`}>
                    {livePrimaryLine}
                  </div>
                </div>
              </div>

              <LiveChatPanel
                selectedEmployeeId={selectedEmployeeId}
                employees={employeeList}
                onSelectEmployee={onSelectEmployee}
                supportStatus={supportStatus}
                showStatusBadge={false}
                suppressBusyBanner
                documents={documents}
                onOpenSopDocument={onOpenSopDocument}
                sharedChat={liveChatShared || undefined}
              />

              {/* Secondary disclosure — tracked ticket. Collapsed by
                  default so it doesn't compete with the chat. */}
              {!ticketDisclosed && (
                <button
                  type="button"
                  onClick={() => setTicketDisclosed(true)}
                  className="w-full text-left text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between"
                >
                  <span>
                    <span className="font-semibold text-gray-800 underline">Click here</span>
                    <span> to submit a tracked ticket — keeps a permanent thread you can reply to later.</span>
                  </span>
                  <span className="text-gray-400 ml-2">→</span>
                </button>
              )}
            </div>
          )}

          {/* Shared ticket form — renders below Live chat when
              disclosed, or as the primary path in offline mode. */}
          {showTicketForm && (
            <div className={liveAvailable ? 'rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3' : 'space-y-3'}>
              {liveAvailable && (
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">Submit a tracked ticket</div>
                  <button
                    type="button"
                    onClick={() => setTicketDisclosed(false)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Hide
                  </button>
                </div>
              )}

              <div className="space-y-2">
                <div className="text-sm font-semibold">Category (required)</div>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => {
                    setSelectedCategoryId(e.target.value);
                    setMessage('');
                    setConfirmText('');
                    setDescribeFocused(false);
                  }}
                  className="w-full p-3 border rounded-lg bg-white"
                >
                  <option value="">Select a category…</option>
                  {(categories || []).filter((c) => c.active !== false).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {!ticketCategorySelected && (
                  <div className="text-xs text-gray-500">
                    Pick a category to continue.
                  </div>
                )}
              </div>

              {/* SOP suggestions — offline only, after category
                  chosen, before the user starts typing. Subtle gray
                  treatment so it reads as a hint, not an alert. */}
              {ticketReady && !describeFocused && sopSuggestions.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Try these first</div>
                  </div>
                  <div className="space-y-1.5">
                    {sopSuggestions.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => onOpenSopDocument?.(d)}
                        className="w-full text-left flex items-center justify-between gap-3 p-2 rounded-md bg-gray-50 border border-gray-200 hover:border-gray-300 hover:bg-white transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{d.name}</div>
                          {d.description ? (
                            <div className="text-xs text-gray-500 truncate">{d.description}</div>
                          ) : null}
                        </div>
                        <span className="text-xs text-gray-500 shrink-0">Open →</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Describe + submit */}
              {ticketReady && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold">Describe the issue (required)</div>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onFocus={() => setDescribeFocused(true)}
                    placeholder="Include any key details…"
                    className="w-full min-h-28 p-3 border rounded-lg bg-white"
                    disabled={sending}
                  />
                  <button
                    type="button"
                    onClick={handleSendTicket}
                    disabled={!message.trim() || sending}
                    className={`w-full py-3 rounded-lg font-semibold ${(!message.trim() || sending) ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                  >
                    Send Issue Notification
                  </button>
                  {mode === 'offline' && (
                    <div className="text-xs text-gray-700">
                      For emergencies, contact Jeff.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {confirmText && (
            <div className="p-3 rounded-lg border bg-green-50 text-green-900 text-sm">
              {confirmText}
            </div>
          )}
        </div>

        <div className="p-3 border-t bg-white text-[11px] text-gray-500">
          Track, reply to, and follow up on your tickets from the <span className="font-semibold">Tickets</span> button in the top bar.
        </div>
      </div>

      {helpOpen && (
        <div
          className="absolute inset-0 z-[95] flex items-end sm:items-center justify-center p-4 sm:p-6"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close how it works"
            onClick={() => setHelpOpen(false)}
          />
          <div
            id={SUPPORT_HELP_PANEL_ID}
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-drawer-how-it-works-title"
            className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl p-4 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 mb-3">
              <h2 id="support-drawer-how-it-works-title" className="text-base font-semibold text-gray-900 pr-2">
                How Remote Support works
              </h2>
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                className="shrink-0 p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {SUPPORT_HELP_SECTIONS.map((section) => (
                <div key={section.title}>
                  <div className="text-sm font-semibold text-gray-900">{section.title}</div>
                  <div className="text-xs text-gray-700 mt-1 leading-relaxed">{section.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
